import { useState } from 'react'
import { Card } from '@/components/ui/Card'
import { ChevronDown, ChevronUp, Target, ArrowRight, Users, FileText, CheckCircle2, Monitor, Send, PenTool, Building2, GitBranch, Lock } from 'lucide-react'

type ProcessTab = 'new-business' | 'upsells' | 'resellers'

export function PresalesProcessGoal() {
  const [isOpen, setIsOpen] = useState(false)
  const [tab, setTab] = useState<ProcessTab>('new-business')

  const tabs: { key: ProcessTab; label: string }[] = [
    { key: 'new-business', label: 'New Business' },
    { key: 'upsells', label: 'Upsells' },
    { key: 'resellers', label: 'Resellers' },
  ]

  return (
    <Card className="overflow-hidden border border-blue-800/20">
      {/* Collapsible Header */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-5 py-3 flex items-center justify-between bg-blue-900/10 hover:bg-blue-900/20 transition-colors"
      >
        <div className="flex items-center gap-2">
          <Target className="h-4 w-4 text-blue-400" />
          <span className="text-xs font-semibold uppercase tracking-wider text-blue-400">Goal</span>
          <span className="text-sm font-medium text-[var(--text-primary)]">
            Establish What Good Looks Like in Presales
          </span>
        </div>
        {isOpen ? (
          <ChevronUp className="h-4 w-4 text-[var(--text-muted)]" />
        ) : (
          <ChevronDown className="h-4 w-4 text-[var(--text-muted)]" />
        )}
      </button>

      {/* Content */}
      {isOpen && (
        <div className="border-t border-blue-800/20">
          {/* Tab bar */}
          <div className="flex border-b border-[var(--border-color)] px-5">
            {tabs.map((t) => (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`px-4 py-2 text-sm font-medium transition-colors relative ${
                  tab === t.key ? 'text-[var(--text-primary)]' : 'text-[var(--text-muted)] hover:text-[var(--text-secondary)]'
                }`}
              >
                {t.label}
                {tab === t.key && <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-500 rounded-full" />}
              </button>
            ))}
          </div>

          <div className="p-5 space-y-4">
            {tab === 'new-business' && <NewBusinessProcess />}
            {tab === 'upsells' && <UpsellProcess />}
            {tab === 'resellers' && <ResellerProcess />}
          </div>
        </div>
      )}
    </Card>
  )
}

function NewBusinessProcess() {
  return (
    <div className="space-y-3">
      <p className="text-sm text-[var(--text-secondary)] leading-relaxed">
        The standard presales lifecycle for new customers, from initial engagement through to implementation handover.
      </p>

      <div className="space-y-3">
        <ProcessStep
          icon={<Users className="h-4 w-4" />}
          step="1"
          title="SC Assigned & Research"
          description="A Solutions Consultant is assigned to the deal. They research the customer to prepare an effective first demo using Miro diagrams, API examples, GitHub integration examples, or a portal demo."
          color="blue"
        />
        <StepArrow />
        <ProcessStep
          icon={<Monitor className="h-4 w-4" />}
          step="2"
          title="Demo & Information Supplied"
          description="The demo is conducted and supporting information is supplied. The customer can be given access to the sandbox environment &mdash; a shared public environment where customers can quickly explore and test FrankieOne capabilities. This ensures implementations starts from a clean slate when setting up the customer's own configuration."
          color="blue"
        />
        <StepArrow />
        <ProcessStep
          icon={<FileText className="h-4 w-4" />}
          step="3"
          title="Scope of Work Established"
          description="The goal of pre-sales is to establish the scope of work. The scope is created in DealHub and captures all elements of the customer's configuration, preferred go-live dates, any custom work, and sets expectations about FrankieOne features and capabilities."
          color="blue"
        />
        <StepArrow />
        <ProcessStep
          icon={<CheckCircle2 className="h-4 w-4" />}
          step="4"
          title="Scope 100% Known & Contract Signed"
          description="Once the scope is fully known and the customer has signed their contract, a formal handover meeting is held. On the call, the scope of work is walked through with implementations and the customer."
          color="blue"
        />
        <StepArrow />
        <ProcessStep
          icon={<PenTool className="h-4 w-4" />}
          step="5"
          title="Implementations Review & SOW Sign-off"
          description="Implementations reviews the statement of work and makes any updates to the project plan (in the SOW tab in the scope). Once they've signed off, the SOW is exported via DealHub and added to Annature (DocuSign tool), then sent to the customer and head of implementations to sign."
          color="blue"
        />
        <StepArrow />
        <ProcessStep
          icon={<Send className="h-4 w-4" />}
          step="6"
          title="Handed Over to Implementations"
          description="Once the signed SOW comes back from the customer and head of implementations, the customer is considered formally handed over to implementations."
          color="green"
        />
      </div>
    </div>
  )
}

function UpsellProcess() {
  return (
    <div className="space-y-3">
      <p className="text-sm text-[var(--text-secondary)] leading-relaxed">
        The upsell process follows a similar lifecycle to new business, initiated when Customer Success contacts pre-sales for a potential upsell engagement.
      </p>

      <div className="space-y-3">
        <ProcessStep
          icon={<Users className="h-4 w-4" />}
          step="1"
          title="CS Contacts Pre-Sales"
          description="Customer Success or the Account Manager contacts pre-sales about a potential upsell. Pre-sales will understand the customer's existing use case from CS/AM."
          color="blue"
        />
        <StepArrow />
        <ProcessStep
          icon={<Monitor className="h-4 w-4" />}
          step="2"
          title="Demos & Technical Walkthroughs"
          description="Together, the Account Manager and Solutions Consultant run demos and technical walkthroughs of FrankieOne products with the customer, focused on the new capabilities being upsold."
          color="blue"
        />
        <StepArrow />
        <ProcessStep
          icon={<FileText className="h-4 w-4" />}
          step="3"
          title="Upsell Scope Created"
          description="The scope is created with the upsell setting enabled, which means it only captures new changes to the customer's configuration &mdash; not their existing scope, only the new scope being added."
          color="blue"
        />
        <StepArrow />
        <ProcessStep
          icon={<CheckCircle2 className="h-4 w-4" />}
          step="4"
          title="Scope Confirmed & SOW Export"
          description="The Solutions Consultant ticks a manual confirmation to mark the scope as 100% known. The statement of work can now be exported from DealHub."
          color="blue"
        />
        <StepArrow />
        <ProcessStep
          icon={<PenTool className="h-4 w-4" />}
          step="5"
          title="Handover & SOW Signing"
          description="Once the upsell contract is signed, a handover meeting is held with the implementations team to walk through the new statement of work. The SOW is then sent to the customer to sign the new agreement."
          color="blue"
        />
        <StepArrow />
        <ProcessStep
          icon={<Send className="h-4 w-4" />}
          step="6"
          title="Handed Over to Implementations"
          description="Once the customer signs the new agreement, they are considered handed over to implementations for the upsell work."
          color="green"
        />
      </div>
    </div>
  )
}

function ResellerProcess() {
  return (
    <div className="space-y-3">
      <p className="text-sm text-[var(--text-secondary)] leading-relaxed">
        Reseller agreements have a parent-child relationship model that enforces configuration consistency across reseller customers.
      </p>

      <div className="space-y-3">
        <ProcessStep
          icon={<Building2 className="h-4 w-4" />}
          step="1"
          title="Reseller Scope Setup"
          description="In the Advanced tab of a deal's scope, the reseller section defines the relationship. A scope can be marked as a reseller parent, which will inform the scopes of all its reseller children."
          color="violet"
        />
        <StepArrow />
        <ProcessStep
          icon={<GitBranch className="h-4 w-4" />}
          step="2"
          title="Parent-Child Relationships"
          description="Reseller parents can have child scopes for different deals. Child scopes inherit configuration from the parent and cannot be independently changed &mdash; only the parent reseller scope can be edited. This enforces reseller agreement terms across all children."
          color="violet"
        />
        <StepArrow />
        <ProcessStep
          icon={<Lock className="h-4 w-4" />}
          step="3"
          title="Configuration Enforcement"
          description="The parent-child model ensures that reseller agreements are consistently enforced. Configuration requirements defined in the parent scope automatically apply to all child scopes, preventing drift."
          color="violet"
        />
        <StepArrow />
        <ProcessStep
          icon={<FileText className="h-4 w-4" />}
          step="4"
          title="Reseller SOW & Pre-Sales Process"
          description="Every reseller agreement goes through the standard pre-sales process: establish the scope of work, get it signed, and formally hand over to implementations."
          color="violet"
        />
        <StepArrow />
        <ProcessStep
          icon={<Send className="h-4 w-4" />}
          step="5"
          title="Handover & Ongoing Management"
          description="After handover, implementations sets up the reseller configuration. Once live, the customer goes back through to Customer Success for ongoing management."
          color="green"
        />
      </div>
    </div>
  )
}

function ProcessStep({
  icon,
  step,
  title,
  description,
  color,
}: {
  icon: React.ReactNode
  step: string
  title: string
  description: string
  color: 'blue' | 'green' | 'violet'
}) {
  const colorClasses = {
    green: 'bg-green-900/20 border-green-700/40 text-green-400',
    blue: 'bg-blue-900/20 border-blue-700/40 text-blue-400',
    violet: 'bg-violet-900/20 border-violet-700/40 text-violet-400',
  }[color]

  return (
    <div className={`rounded-lg border p-3 ${colorClasses}`}>
      <div className="flex items-start gap-3">
        <div className={`shrink-0 flex items-center justify-center w-7 h-7 rounded-full border ${colorClasses}`}>
          <span className="text-xs font-bold">{step}</span>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 mb-0.5">
            {icon}
            <span className="font-semibold text-sm text-[var(--text-primary)]">{title}</span>
          </div>
          <p
            className="text-xs text-[var(--text-secondary)] leading-relaxed"
            dangerouslySetInnerHTML={{ __html: description }}
          />
        </div>
      </div>
    </div>
  )
}

function StepArrow() {
  return (
    <div className="flex justify-center">
      <ArrowRight className="h-4 w-4 text-[var(--text-muted)] rotate-90" />
    </div>
  )
}
